import type { NextPage } from 'next';
import React from 'react';
import { BannerFormWrap } from '@hotelplan/components.common.banner-form';
import { FdrTextBlockSkeleton } from '@hotelplan/fdr.regular.basis.fdr-text-block';
import {
  FdrMediaGallerySkeleton,
  HEIGHT_DESKTOP,
  HEIGHT_MOBILE,
} from '@hotelplan/fdr.regular.fusion.fdr-media-gallery';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { trackSearch } from '@hotelplan/libs.tracking';
import { fdrThemeVars } from 'components/page/theme/theme.vars';
import { FdrHero } from 'fdr/components/candidate/fdr-hero';
import { ESearchType } from 'fdr/components/candidate/fdr-search-control/destination/fdr-destination-field.types';
import { FdrSearchControlFields } from 'fdr/components/candidate/fdr-search-control/fdr-search-control-fields';
import { mapSearchControlStateToTrackableData } from 'fdr/components/candidate/fdr-search/mappers/fdr-search-control.mappers';
import {
  fdrCommonPageComponentsVariables,
  FdrPageComponents,
  useFdrPageComponentsContext,
} from 'fdr/components/local/fdr-page-components';
import { FdrRecommendationListSkeleton } from 'fdr/components/local/fdr-recommendation-list/fdr-recommendation-list.skeleton';
import { FdrThemeLeadTextBlock } from 'fdr/components/local/fdr-theme/fdr-theme-overview.styles';
import {
  FdrMediaGalleryWrapper,
  FdrTextBlockSkeletonWrapper,
} from 'fdr/components/local/fdr-theme/fdr-theme.styles';
import { EThemeFormPageType } from 'fdr/components/local/fdr-theme/fdr-theme.types';
import { FdrThemeFormActionToggler } from 'fdr/components/local/fdr-theme/search-form/fdr-theme-form-action-toggler';
import FdrThemeSearchForm from 'fdr/components/local/fdr-theme/search-form/fdr-theme-search-form';
import { useFdrThemePageComponentsQuery } from 'fdr/schemas/query/theme/page-components/fdr-theme-page-components.generated';
import { useFdrThemePageContentQuery } from 'fdr/schemas/query/theme/page-content/fdr-theme-page-content.generated';

export { getServerSideProps } from 'components/page/theme/theme.gssp';

const ThemePage: NextPage = () => {
  const { mobile } = useDeviceType();
  const { id } = useObjectId();
  const { data } = useFdrThemePageContentQuery({
    variables: {
      input: {
        publicId: id,
      },
      ...fdrThemeVars,
    },
  });
  const { hero, text, name, textBgColor, textFgColor, textBackgroundImage } =
    data?.fdrTheme || {};

  const context = useFdrPageComponentsContext();
  const { data: pageComponents, loading: pageComponentsLoading } =
    useFdrThemePageComponentsQuery({
      variables: {
        input: {
          publicId: id,
        },
        ...fdrCommonPageComponentsVariables,
        ...context,
      },
    });

  const components = pageComponents?.fdrTheme?.pageComponents?.components || [];

  return (
    <>
      <BannerFormWrap>
        {hero && <FdrHero heroImage={hero} />}
        {mobile && (
          <FdrThemeFormActionToggler
            id={id}
            pageType={EThemeFormPageType.THEME}
          />
        )}
        {!mobile && (
          <FdrThemeSearchForm
            id={id}
            pageType={EThemeFormPageType.THEME}
            postSubmit={function (state) {
              trackSearch({
                searchControl: mapSearchControlStateToTrackableData(state),
              });
            }}
          >
            <FdrSearchControlFields id={id} searchType={ESearchType.THEME} />
          </FdrThemeSearchForm>
        )}
      </BannerFormWrap>
      {text && (
        <FdrThemeLeadTextBlock
          title={name || ''}
          text={text || ''}
          backgroundImage={textBackgroundImage}
          backgroundColor={textBgColor}
          fontColor={textFgColor}
        />
      )}
      {pageComponentsLoading && !pageComponents ? (
        <>
          <FdrTextBlockSkeletonWrapper>
            <FdrTextBlockSkeleton
              uniqueKey="homeTextBlock"
              className="text-block-page"
            />
          </FdrTextBlockSkeletonWrapper>
          <FdrRecommendationListSkeleton />
          <FdrRecommendationListSkeleton />
          <FdrMediaGalleryWrapper>
            <FdrMediaGallerySkeleton
              uniqueKey="mediaGallerySkeleton"
              height={mobile ? HEIGHT_MOBILE : HEIGHT_DESKTOP}
            />
          </FdrMediaGalleryWrapper>
        </>
      ) : (
        <FdrPageComponents components={components} />
      )}
    </>
  );
};

export default ThemePage;
